/**
 * Anchor-measurement hook for `CommentsGutter` (#2244).
 *
 * Pulled out of `comments-gutter.tsx` so the gutter file stays under
 * the 300-line budget the ticket's DoD asks for. Owns the DOM
 * measurement loop: for each entry, finds the ancestor paragraph of
 * its anchor word (last word of the comment range) and records its
 * bottom + right edge inside the scroll container. The gutter uses
 * these to pin the card's bottom-right corner to the paragraph's
 * bottom-right corner.
 *
 * `null` means the paragraph is currently virtualised out of the DOM
 * — the caller should skip rendering that card.
 */

import { useCallback, useEffect, useLayoutEffect, useState } from 'react';

export interface AnchorEntryInput {
  key: string;
  indexFrom: string | undefined;
  indexTo: string | undefined;
}

export interface AnchorRect {
  /** Viewport-relative top edge inside the scroll container. Used by
   *  the gutter stacking (#2244) — cards align to the top of their
   *  anchor word so a comment sits level with the line that spawned
   *  it, not with the end of that line. */
  top: number;
  /** Viewport-relative bottom edge inside the scroll container. */
  bottom: number;
  /** Offset from the scroll container's right edge to the paragraph's
   *  right edge — same units as `right: Npx` would use. */
  right: number;
}

function readAnchorRectFor(
  indexFrom: string | undefined,
  indexTo: string | undefined,
  scrollRoot: HTMLElement
): AnchorRect | null {
  const findWord = (attr: 'start' | 'stop', value: string) =>
    scrollRoot.querySelector<HTMLElement>(
      `[data-${attr}="${CSS.escape(value)}"]`
    );
  const anchorWord =
    (indexTo ? findWord('stop', indexTo) : null) ??
    (indexFrom ? findWord('start', indexFrom) : null);
  if (!anchorWord) return null;
  // Anchor to the last word of the selection — the card's
  // bottom-right lines up with where the user's selection stops.
  // Earlier we walked up to the paragraph row, but on a one-line
  // paragraph `card_top = paragraph.bottom - card_height` dropped
  // the card up above the row entirely, where it overlapped the
  // paragraph above. Pinning to the end-word keeps the card inside
  // the row it belongs to regardless of paragraph height.
  const rootRect = scrollRoot.getBoundingClientRect();
  const wordRect = anchorWord.getBoundingClientRect();
  return {
    top: wordRect.top - rootRect.top,
    bottom: wordRect.bottom - rootRect.top,
    right: rootRect.right - wordRect.right
  };
}

function sameRect(a: AnchorRect | null, b: AnchorRect | null): boolean {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  return a.top === b.top && a.bottom === b.bottom && a.right === b.right;
}

/**
 * Returns a map of `entry.key -> AnchorRect | null`, recomputed on
 * every scroll frame + resize + character-level mutation inside the
 * scroll root.
 */
export function useAnchorTracker(
  entries: AnchorEntryInput[],
  scrollRootRef: React.RefObject<HTMLElement | null>
): Record<string, AnchorRect | null> {
  const [rects, setRects] = useState<Record<string, AnchorRect | null>>({});

  const recompute = useCallback(() => {
    const scrollRoot = scrollRootRef.current;
    if (!scrollRoot) return;
    const next: Record<string, AnchorRect | null> = {};
    for (const entry of entries) {
      next[entry.key] = readAnchorRectFor(
        entry.indexFrom,
        entry.indexTo,
        scrollRoot
      );
    }
    setRects(prev => {
      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(next);
      if (prevKeys.length === nextKeys.length) {
        let same = true;
        for (const k of nextKeys) {
          if (!sameRect(prev[k], next[k])) {
            same = false;
            break;
          }
        }
        if (same) return prev;
      }
      return next;
    });
  }, [entries, scrollRootRef]);

  useLayoutEffect(() => {
    recompute();
  }, [recompute]);

  useEffect(() => {
    const scrollRoot = scrollRootRef.current;
    if (!scrollRoot) return;

    let raf = 0;
    const schedule = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        recompute();
      });
    };

    scrollRoot.addEventListener('scroll', schedule, { passive: true });
    const resizeObserver = new ResizeObserver(schedule);
    resizeObserver.observe(scrollRoot);
    const mutationObserver = new MutationObserver(schedule);
    mutationObserver.observe(scrollRoot, {
      childList: true,
      subtree: true,
      characterData: true
    });

    return () => {
      scrollRoot.removeEventListener('scroll', schedule);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [recompute, scrollRootRef]);

  return rects;
}
